import React, { useState, Children } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
// Import note (audit-referentie: Ronde 10): `Children` is NOT used in this
// file. Regressed in ronde 6 and ronde 8 — see `.eslintrc.json` for the
// lint rule that should prevent this once lint is wired into CI.
import { DjayAvatar, DjayBubble, djayVariants, getDjayMissionHeading, getDjayLabWelcome, getDjayModeIntro } from '.';
import type { DjaySize, DjayEmotion, DjayTier, DjayVariant, BubbleColorScheme } from '.';
const SIZES: {
  size: DjaySize;
  label: string;
  pixels: number;
}[] = [{
  size: 'xs',
  label: 'XS',
  pixels: 16
}, {
  size: 'sm',
  label: 'SM',
  pixels: 24
}, {
  size: 'md',
  label: 'MD',
  pixels: 32
}, {
  size: 'lg',
  label: 'LG',
  pixels: 48
}, {
  size: 'xl',
  label: 'XL',
  pixels: 64
}, {
  size: 'hero',
  label: 'Hero',
  pixels: 128
}];
const EMOTIONS: {
  emotion: DjayEmotion;
  label: string;
  icon: string;
}[] = [{
  emotion: 'happy',
  label: 'Happy',
  icon: '😊'
}, {
  emotion: 'excited',
  label: 'Excited',
  icon: '🤩'
}, {
  emotion: 'encouraging',
  label: 'Encouraging',
  icon: '🤗'
}, {
  emotion: 'celebrating',
  label: 'Celebrating',
  icon: '🎉'
}, {
  emotion: 'proud',
  label: 'Proud',
  icon: '🌟'
}, {
  emotion: 'thinking',
  label: 'Thinking',
  icon: '🤔'
}, {
  emotion: 'sleeping',
  label: 'Sleeping',
  icon: '😴'
}, {
  emotion: 'pointing',
  label: 'Pointing',
  icon: '👉'
}, {
  emotion: 'surprised',
  label: 'Surprised',
  icon: '😲'
}];
const TIERS: {
  tier: DjayTier;
  label: string;
  icon: string;
}[] = [{
  tier: 'rookie',
  label: 'Rookie',
  icon: '🌱'
}, {
  tier: 'pro',
  label: 'Pro',
  icon: '🌿'
}, {
  tier: 'master',
  label: 'Master',
  icon: '👑'
}];
const VARIANTS: {
  variant: DjayVariant;
  label: string;
}[] = [{
  variant: 'default',
  label: 'Default'
}, {
  variant: 'blaze',
  label: 'Blaze'
}, {
  variant: 'frost',
  label: 'Frost'
}, {
  variant: 'pixel',
  label: 'Pixel'
}, {
  variant: 'royal',
  label: 'Royal'
}, {
  variant: 'cosmic',
  label: 'Cosmic'
}, {
  variant: 'shadow',
  label: 'Shadow'
}, {
  variant: 'goud',
  label: 'Goud ✨'
}, {
  variant: 'rainbow',
  label: 'Regenboog 🌈'
}];
const BUBBLE_SCHEMES: {
  scheme: BubbleColorScheme;
  label: string;
}[] = [{
  scheme: 'default',
  label: 'Default'
}, {
  scheme: 'indigo',
  label: 'Indigo'
}, {
  scheme: 'amber',
  label: 'Amber'
}, {
  scheme: 'emerald',
  label: 'Emerald'
}, {
  scheme: 'violet',
  label: 'Violet'
}];
const MODE_IDS = ['fluency', 'sprint', 'challenge', 'experiments', 'assessment'];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: 'spring',
      stiffness: 300,
      damping: 24
    }
  }
};
export function DjayShowcase() {
  const [activeSize, setActiveSize] = useState<DjaySize>('hero');
  const [activeEmotion, setActiveEmotion] = useState<DjayEmotion>('happy');
  const [activeTier, setActiveTier] = useState<DjayTier>('rookie');
  const [activeVariant, setActiveVariant] = useState<DjayVariant>('default');
  const [animationsEnabled, setAnimationsEnabled] = useState(true);
  const currentSizeConfig = SIZES.find(s => s.size === activeSize);
  const currentVariantConfig = VARIANTS.find(v => v.variant === activeVariant);
  const currentEmotionConfig = EMOTIONS.find(e => e.emotion === activeEmotion);
  const currentTierConfig = TIERS.find(t => t.tier === activeTier);
  return <div className="h-full overflow-y-auto bg-slate-50" data-id="element-3443" data-mp="odhu5i0ydqp9">
      <motion.div className="max-w-6xl mx-auto p-6 pb-16 space-y-8" variants={containerVariants} initial="hidden" animate="visible" data-id="element-3444" data-mp="1jmmxb7ijcg4o">
        {/* Section 1: Header */}
        <motion.div variants={itemVariants} className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm" data-id="element-3445" data-mp="1skf5zstr4pj3">
          <div className="flex items-center gap-5" data-id="element-3446" data-mp="22jxn81ia1x7g">
            <motion.div variants={djayVariants.celebrate} animate="animate" transition={{
            repeat: Infinity,
            repeatDelay: 3
          }} data-id="element-3447" data-mp="26l9hwlg0d8mi">
              <DjayAvatar size="xl" emotion="happy" animate={animationsEnabled} data-id="element-3448" data-mp="2gb2qddkut7mj" />
            </motion.div>
            <div data-id="element-3449" data-mp="2q98nlhvptpl2">
              <h1 className="text-2xl font-bold text-gray-900" data-id="element-3450" data-mp="2qatij8lxzajg">
                Mascotte Djay
              </h1>
              <p className="text-gray-500 text-sm mt-1" data-id="element-3451" data-mp="enzvbzxanf4q">
                Beheer en preview van de Djay mascotte componenten.
              </p>
            </div>
          </div>
          <button onClick={() => setAnimationsEnabled(!animationsEnabled)} className={`text-sm font-medium px-4 py-2 rounded-full transition-colors whitespace-nowrap ${animationsEnabled ? 'bg-orange-100 text-orange-700 hover:bg-orange-200' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3452" data-mp="4nfs4rq7s0jv">
            {animationsEnabled ? '✨ Animaties aan' : '⏸ Animaties uit'}
          </button>
        </motion.div>

        {/* Section 2: Unified Configurator */}
        <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3453" data-mp="iojsh3hvaiqv">
          <div className="px-6 py-4 border-b border-gray-100" data-id="element-3454" data-mp="1g5h4uifgwe8a">
            <h2 className="font-semibold text-gray-900" data-id="element-3455" data-mp="1bbw26d2xiw2r">Configurator</h2>
          </div>

          <div className="p-6 flex flex-col lg:flex-row gap-8" data-id="element-3456" data-mp="35lkqou324g5i">
            {/* Controls */}
            <div className="flex-1 space-y-6" data-id="element-3457" data-mp="3ajw2hacwx0qd">
              {/* Emotion Picker */}
              <div data-id="element-3458" data-mp="2vv899aqjh33l">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3459" data-mp="2vtoszk4vus1t">
                  Emotie
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-3460" data-mp="30s8ejjfke1tn">
                  {EMOTIONS.map(({
                  emotion,
                  label,
                  icon
                }) => <button key={emotion} onClick={() => setActiveEmotion(emotion)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${activeEmotion === emotion ? 'bg-orange-500 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3461" data-mp="2mw4qy8z74rq5">
                      <span data-id="element-3462" data-mp="b5aknep0es3x">{icon}</span>
                      <span data-id="element-3463" data-mp="g43wl2oneb1z">{label}</span>
                    </button>)}
                </div>
              </div>

              {/* Tier Picker */}
              <div data-id="element-3464" data-mp="20jiizaa17igt">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3465" data-mp="1vmjkygn8yrdc">Tier</h3>
                <div className="inline-flex bg-gray-100 p-1 rounded-xl" data-id="element-3466" data-mp="1vkfdjoq53a8b">
                  {TIERS.map(({
                  tier,
                  label,
                  icon
                }) => <button key={tier} onClick={() => setActiveTier(tier)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${activeTier === tier ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3467" data-mp="25k645fbkx534">
                      <span data-id="element-3468" data-mp="2t87kvbiwvrtm">{icon}</span>
                      <span data-id="element-3469" data-mp="2oanwvjwew0oh">{label}</span>
                    </button>)}
                </div>
              </div>

              {/* Variant Picker */}
              <div data-id="element-3470" data-mp="yy3wyozscq2c">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3471" data-mp="txfxrg1el2x1">
                  Variant (Skin)
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-3472" data-mp="82z5oumafj17">
                  {VARIANTS.map(({
                  variant,
                  label
                }) => <button key={variant} onClick={() => setActiveVariant(variant)} className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${activeVariant === variant ? 'bg-indigo-600 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3473" data-mp="d1sh8hi0kzyq">
                      {label}
                    </button>)}
                </div>
              </div>

              {/* Size Picker */}
              <div data-id="element-3474" data-mp="3v7f33flw9ubl">
                <h3 className="text-sm font-medium text-gray-700 mb-3" data-id="element-3475" data-mp="34xn0asrxlq9">
                  Formaat
                </h3>
                <div className="flex flex-wrap gap-2" data-id="element-3476" data-mp="1aj0povr5z46u">
                  {SIZES.map(({
                  size,
                  label,
                  pixels
                }) => <button key={size} onClick={() => setActiveSize(size)} className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${activeSize === size ? 'bg-slate-800 text-white shadow-sm' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-id="element-3477" data-mp="1ejb48wt0v2xb">
                      {label}{' '}
                      <span className="opacity-70 font-normal" data-id="element-3478" data-mp="2lb8jqz8ns0fh">
                        ({pixels}px)
                      </span>
                    </button>)}
                </div>
              </div>
            </div>

            {/* Active Preview */}
            <div className="flex flex-col items-center justify-center bg-gradient-to-br from-orange-50 to-amber-50 rounded-2xl border-2 border-dashed border-orange-200 p-8 min-w-[300px] lg:w-1/3" data-id="element-3479" data-mp="2bevohwxgteim">
              <div className="flex-1 flex items-center justify-center min-h-[160px]" data-id="element-3480" data-mp="22e7bn5jt5k7b">
                <AnimatePresence mode="wait" data-id="element-3481" data-mp="5hhx6kwemgsh">
                  <motion.div key={`${activeSize}-${activeEmotion}-${activeTier}-${activeVariant}`} initial={{
                  scale: 0.8,
                  opacity: 0
                }} animate={{
                  scale: 1,
                  opacity: 1
                }} exit={{
                  scale: 0.8,
                  opacity: 0
                }} transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 25
                }} className="flex items-center justify-center" data-id="element-3482" data-mp="k7zyj2rx0u3">
                    <DjayAvatar size={activeSize} emotion={activeEmotion} tier={activeTier} variant={activeVariant} animate={animationsEnabled} data-id="element-3483" data-mp="2nubvz6eaibw7" />
                  </motion.div>
                </AnimatePresence>
              </div>
              <div className="mt-6 text-center bg-white/80 backdrop-blur-sm px-4 py-2 rounded-xl border border-orange-100 shadow-sm" data-id="element-3484" data-mp="5ussa16der61">
                <div className="text-sm font-bold text-gray-800" data-id="element-3485" data-mp="anlxsr9ny3j6">
                  {currentTierConfig?.label} · {currentVariantConfig?.label}
                </div>
                <div className="text-xs text-gray-500 mt-0.5" data-id="element-3486" data-mp="1t7gyk9hikxy">
                  {currentEmotionConfig?.label} · {currentSizeConfig?.label} (
                  {currentSizeConfig?.pixels}px)
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section 3: Matrix Overview */}
        <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3487" data-mp="fkcffj14976p">
          <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center" data-id="element-3488" data-mp="30bmk58d6vnqx">
            <div data-id="element-3489" data-mp="35a6adkytflp3">
              <h2 className="font-semibold text-gray-900" data-id="element-3490" data-mp="35apqcw4wo00h">
                Tier × Variant Matrix
              </h2>
              <p className="text-xs text-gray-500 mt-0.5" data-id="element-3491" data-mp="2l71okr6mlfxy">
                Klik op een cel om deze in de configurator te laden. Toont
                huidige emotie: {currentEmotionConfig?.icon}{' '}
                {currentEmotionConfig?.label}.
              </p>
            </div>
          </div>
          <div className="p-6 overflow-x-auto" data-id="element-3492" data-mp="27g59m35mgx9d">
            <div className="min-w-[800px]" data-id="element-3493" data-mp="22htzrsfbumr5">
              {/* Header Row */}
              <div className="grid grid-cols-[120px_repeat(8,1fr)] gap-2 mb-2" data-id="element-3494" data-mp="22j50fjdh7ee1">
                <div data-id="element-3495" data-mp="1xh7cibu4rver"></div>
                {VARIANTS.map(v => <div key={v.variant} className="text-center text-xs font-bold text-gray-500 uppercase tracking-wider" data-id="element-3496" data-mp="1tgdo7hcs0elc">
                    {v.label}
                  </div>)}
              </div>

              {/* Tier Rows */}
              <div className="space-y-2" data-id="element-3497" data-mp="3ivty4yyiy4ca">
                {TIERS.map(t => <div key={t.tier} className="grid grid-cols-[120px_repeat(8,1fr)] gap-2 items-center" data-id="element-3498" data-mp="3qfw26uspt234">
                    <div className="text-sm font-semibold text-gray-700 flex items-center gap-2 bg-gray-50 p-2 rounded-lg border border-gray-100" data-id="element-3499" data-mp="3vcmk7no2y7r6">
                      <span data-id="element-3500" data-mp="8ze8fngtdua8">{t.icon}</span>
                      <span data-id="element-3501" data-mp="8ybh5efkxpts">{t.label}</span>
                    </div>
                    {VARIANTS.map(v => {
                  const isSelected = activeTier === t.tier && activeVariant === v.variant;
                  return <button key={`${t.tier}-${v.variant}`} onClick={() => {
                    setActiveTier(t.tier);
                    setActiveVariant(v.variant);
                  }} className={`flex justify-center items-center p-3 rounded-xl border transition-all ${isSelected ? 'bg-orange-50 border-orange-400 ring-2 ring-orange-400 ring-offset-1' : 'bg-white border-gray-100 hover:border-gray-300 hover:bg-gray-50'}`} data-id="element-3502" data-mp="3hdv826ss2w5o">
                          <DjayAvatar size="md" emotion={activeEmotion} tier={t.tier} variant={v.variant} animate={animationsEnabled} data-id="element-3503" data-mp="197b9bu0nxela" />
                        </button>;
                })}
                  </div>)}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section 4: Animations & Bubbles */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-3504" data-mp="1mzr5cav5cccp">
          {/* Animations */}
          <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3505" data-mp="20xpk3hddqasq">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-3506" data-mp="2fl6baiy3y1k4">
              <h2 className="font-semibold text-gray-900" data-id="element-3507" data-mp="2flpm7fpcbpon">
                Animaties (Framer Motion)
              </h2>
            </div>
            <div className="p-6 grid grid-cols-3 gap-4" data-id="element-3508" data-mp="26khwvn4sxz6p">
              <div className="text-center" data-id="element-3509" data-mp="26jzppvglq0k0">
                <div className="bg-green-50 rounded-xl p-4 flex items-center justify-center h-32 mb-2" data-id="element-3510" data-mp="2bjccyhbdx9n4">
                  <motion.div variants={djayVariants.bounce} animate="animate" transition={{
                  repeat: Infinity,
                  repeatDelay: 2
                }} data-id="element-3511" data-mp="1wuyai7hvrn73">
                    <DjayAvatar size="lg" emotion="excited" animate={false} data-id="element-3512" data-mp="1ns708b0qqxh1" />
                  </motion.div>
                </div>
                <div className="text-xs font-medium text-gray-700" data-id="element-3513" data-mp="1dvaozxjgjx8s">Bounce</div>
              </div>
              <div className="text-center" data-id="element-3514" data-mp="1ivgk5noxs1vm">
                <div className="bg-amber-50 rounded-xl p-4 flex items-center justify-center h-32 mb-2" data-id="element-3515" data-mp="1iudqj808blc0">
                  <motion.div variants={djayVariants.celebrate} animate="animate" transition={{
                  repeat: Infinity,
                  repeatDelay: 2
                }} data-id="element-3516" data-mp="3kle1kyf2lp9h">
                    <DjayAvatar size="lg" emotion="celebrating" animate={false} data-id="element-3517" data-mp="36tzeogi1l3x5" />
                  </motion.div>
                </div>
                <div className="text-xs font-medium text-gray-700" data-id="element-3518" data-mp="3cqz63aseg5l">
                  Celebrate
                </div>
              </div>
              <div className="text-center" data-id="element-3519" data-mp="3pjftm8uh4yx3">
                <div className="bg-indigo-50 rounded-xl p-4 flex items-center justify-center h-32 mb-2" data-id="element-3520" data-mp="3pie97drkj02p">
                  <motion.div variants={djayVariants.tierEvolution} animate="animate" transition={{
                  repeat: Infinity,
                  repeatDelay: 2
                }} data-id="element-3521" data-mp="2j4olpl6xodtk">
                    <DjayAvatar size="lg" emotion="surprised" animate={false} data-id="element-3522" data-mp="29085urtgewpr" />
                  </motion.div>
                </div>
                <div className="text-xs font-medium text-gray-700" data-id="element-3523" data-mp="2dw6r46jhjyly">
                  Evolution
                </div>
              </div>
            </div>
          </motion.div>

          {/* Bubbles */}
          <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3524" data-mp="2mso2navrhyi8">
            <div className="px-6 py-4 border-b border-gray-100" data-id="element-3525" data-mp="2wq2knexc2vcn">
              <h2 className="font-semibold text-gray-900" data-id="element-3526" data-mp="31nkjgchmzu6x">Speech Bubbles</h2>
            </div>
            <div className="p-6 flex flex-col gap-3" data-id="element-3527" data-mp="12azxvozo5rdf">
              {BUBBLE_SCHEMES.map(({
              scheme,
              label
            }) => <div key={scheme} className="flex items-center gap-4" data-id="element-3528" data-mp="179bb89p6ak0e">
                  <div className="w-20 text-xs font-bold text-gray-500 uppercase tracking-wider text-right" data-id="element-3529" data-mp="397ddhu76gvdp">
                    {label}
                  </div>
                  <div className="flex-1" data-id="element-3530" data-mp="347qxxe3vn31q">
                    <DjayBubble message={`Dit is de ${label.toLowerCase()} bubble variant.`} colorScheme={scheme} autoDismissMs={0} position="bottom" className="w-full" data-id="element-3531" data-mp="3ix6kehwdkmbm" />
                  </div>
                </div>)}
            </div>
          </motion.div>
        </div>

        {/* Section 5: Messages Preview */}
        <motion.div variants={itemVariants} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden" data-id="element-3532" data-mp="2zaqpwxngvq7z">
          <div className="px-6 py-4 border-b border-gray-100" data-id="element-3533" data-mp="eif68sr9c30r">
            <h2 className="font-semibold text-gray-900" data-id="element-3534" data-mp="ekt233trhj9d">
              Berichten Registry Preview
            </h2>
          </div>
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-3535" data-mp="i0cq0ggw3ghm">
            <div data-id="element-3536" data-mp="i38lzz96fv3f">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-3537" data-mp="mzpch03umljo">
                Mission Heading
              </div>
              <div className="flex flex-col gap-2" data-id="element-3538" data-mp="849ckegl7vfd">
                {Array.from({
                length: 3
              }).map((_, i) => <div key={i} className="bg-slate-50 text-slate-800 text-sm px-3 py-2 rounded-lg border border-slate-200" data-id="element-3539" data-mp="10rnd0mjhv948" data-mpi={i}>
                    {getDjayMissionHeading()}
                  </div>)}
              </div>
            </div>

            <div data-id="element-3540" data-mp="w05c1yhh18j9">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-3541" data-mp="vzlsyeg34e3n">
                Lab Welcome
              </div>
              <div className="flex flex-col gap-2" data-id="element-3542" data-mp="1jo50usv82ov7">
                {Array.from({
                length: 3
              }).map((_, i) => {
                const msg = getDjayLabWelcome();
                return <div key={i} className="flex items-center gap-2 bg-indigo-50 text-indigo-800 text-sm px-3 py-2 rounded-lg border border-indigo-100" data-id="element-3543" data-mp="3l0vsyox01ht6" data-mpi={i}>
                      <DjayAvatar size="xs" emotion={msg.emotion} animate={false} data-id="element-3544" data-mp="3bakck2w4pg5e" />
                      <span data-id="element-3545" data-mp="31cwgn9mse2ln">{msg.text}</span>
                    </div>;
              })}
              </div>
            </div>

            <div data-id="element-3546" data-mp="2neomf9h4xfe6">
              <div className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3" data-id="element-3547" data-mp="2nf7t1u1c4hcw">
                Mode Intros
              </div>
              <div className="flex flex-col gap-2" data-id="element-3548" data-mp="lm9ztx3nbmrf">
                {MODE_IDS.slice(0, 3).map(modeId => {
                const msg = getDjayModeIntro(modeId);
                return <div key={modeId} className="flex items-start gap-2 bg-amber-50 text-amber-900 text-sm px-3 py-2 rounded-lg border border-amber-100" data-id="element-3549" data-mp="2b2vzukdusnku">
                      <DjayAvatar size="xs" emotion={msg.emotion} animate={false} className="mt-0.5 shrink-0" data-id="element-3550" data-mp="2kt74681g8ys9" />
                      <div data-id="element-3551" data-mp="2ts2cbpo20iy7">
                        <span className="font-bold text-[10px] uppercase block text-amber-700 mb-0.5" data-id="element-3552" data-mp="2tvgfv18gey78">
                          {modeId}
                        </span>
                        {msg.text}
                      </div>
                    </div>;
              })}
              </div>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </div>;
}